import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Plus } from "lucide-react";
import { inr } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { EASE } from "@/components/Reveal";

export default function ProductCard({ product, index = 0 }) {
  const navigate = useNavigate();
  const { addToCart } = useShop();
  const [spot, setSpot] = useState({ x: 50, y: 50 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setSpot({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  const soldOut = product.stock_status === "sold_out";
  const madeToOrder = product.stock_status === "made_to_order";

  return (
    <motion.article
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: EASE }}
      className="group relative card-surface overflow-hidden"
      onMouseMove={onMouseMove}
      data-testid={`product-card-${product._id}`}
    >
      <div
        className="pointer-events-none absolute inset-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `radial-gradient(420px circle at ${spot.x}% ${spot.y}%, rgba(212,175,55,0.16), transparent 55%)` }}
      />
      <button
        className="relative block w-full aspect-[4/5] overflow-hidden bg-bg2 cursor-pointer"
        onClick={() => navigate(`/product/${product._id}`)}
        data-testid={`product-view-${product._id}`}
        aria-label={`View ${product.name}`}
      >
        <img
          src={product.images[0]}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-contain transition-transform duration-[1.2s] ease-out group-hover:scale-[1.08]"
        />
        {(madeToOrder || soldOut) && (
          <span className="absolute left-3 top-3 chip bg-surface/90 backdrop-blur-sm z-10">
            {soldOut ? "Sold Out" : "Made to Order"}
          </span>
        )}
        {product.mrp && product.mrp > product.price && (
          <span className="absolute left-3 bottom-3 z-10 border border-gold/50 bg-deep/90 px-3 py-1 font-mono text-[10px] text-gold2" data-testid={`product-discount-${product._id}`}>
            {Math.round((1 - product.price / product.mrp) * 100)}% OFF
          </span>
        )}
      </button>

      <div className="relative z-10 p-5">
        <div className="flex items-center justify-between">
          <span className="overline-tag">{product.category}</span>
        </div>
        <h3 className="mt-2 font-display text-[22px] font-semibold leading-snug text-ink">
          <button onClick={() => navigate(`/product/${product._id}`)}>{product.name}</button>
        </h3>
        <div className="mt-3.5 flex items-center justify-between">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-[15px] text-ink" data-testid={`product-price-${product._id}`}>{inr(product.price)}</span>
            {product.mrp && product.mrp > product.price && (
              <span className="font-mono text-[11px] text-ink3 line-through">{inr(product.mrp)}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/product/${product._id}`)}
              className="h-9 w-9 grid place-items-center border border-line text-ink2 transition-all duration-300 hover:border-gold hover:text-gold"
              aria-label="Quick view"
              data-testid={`product-quick-view-${product._id}`}
            >
              <Eye size={15} />
            </button>
            <button
              onClick={() => addToCart(product)}
              disabled={soldOut}
              className="h-9 w-9 grid place-items-center bg-gold text-[#241A08] transition-all duration-300 hover:bg-gold2 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={`Add ${product.name} to cart`}
              data-testid={`product-add-to-cart-${product._id}`}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
